import { ButtonPrimary } from '@fukku/button/Primary/ButtonPrimary'
import { ButtonSecondary } from '@fukku/button/Secondary/ButtonSecondary'
import textBodyM from '@fukku/styles/texts/textBodyM.module.css'
import { CART_PATH, SHOPPING_CART_LOCATION } from 'cart/constants/constants'
import { useBagTotalItems } from 'cart/hooks/queries/useBagTotalItems'
import { useCartProduct } from 'cart/hooks/useCartProduct'
import type { ItemDto } from 'cart/services/graphql.types'
import { useNavigation } from 'hooks/useNavigation/useNavigation'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useCheckoutLinks } from 'links/checkout/useCheckoutLinks'
import Image from 'next/image'
import { useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'
import { Price } from '../../../shoppingCart/components/Price/Price'
import { useAnalytics } from '../../hooks/useAnalytics'
import { getProductImageTest } from '../../utils/productMappers'
import styles from './AddedToCartNotificationContent.module.scss'

export const AddedToCartNotificationContent = ({
	item,
	cartItems,
}: {
	readonly item: ItemDto
	readonly cartItems: ItemDto[]
}) => {
	const { cartProduct, productInfo } = useCartProduct(item)
	const { totalItems } = useBagTotalItems()
	const { t } = useLabels()
	const { navigate } = useNavigation()
	const { navigateToCheckout } = useCheckoutLinks()
	const { sendClickViewCart } = useAnalytics()
	const [loaded, setLoaded] = useState(false)
	const { isLargeOrGreater } = useResponsive()
	const { sendBeginCheckoutEECEvent } = useAnalytics()

	if (!cartProduct) {
		return null
	}

	const productImage = getProductImageTest(
		productInfo ?? undefined,
		cartProduct
	)

	return (
		<div className={styles.container}>
			<div className={styles.body}>
				{productImage.img && (
					<div className={styles.imageWrapper}>
						<Image
							alt={productImage.alt}
							data-testid='bag.preview.item.image'
							src={productImage.img}
							width={120}
							height={168}
							onLoad={() => setLoaded(true)}
							className={cx(styles.image, loaded && styles['image--animated'])}
						/>
					</div>
				)}
				<div className={styles.details}>
					<p className={styles.productName}>{cartProduct?.info?.name}</p>
					<div className={styles.price}>
						<Price
							price={cartProduct.price}
							promoCode={undefined}
							textStyle={textBodyM.className}
							horizontal
						/>
					</div>
					<div className={styles.detailsWrapper}>
						<p className={styles.size}>{cartProduct?.info?.sizeName}</p>
						<p className={styles.color}>{cartProduct?.info?.colorName}</p>
					</div>
				</div>
			</div>
			<div className={styles.buttonWrapper}>
				{isLargeOrGreater ? (
					<ButtonSecondary
						data-testid='go-to-checkout-button'
						fullWidth
						className={styles.button}
						onClick={() => {
							void sendBeginCheckoutEECEvent(
								SHOPPING_CART_LOCATION.PREVIEW,
								cartItems
							)
							sendClickViewCart()
							navigateToCheckout()
						}}
					>
						{t('addToBag.checkout.button')}
					</ButtonSecondary>
				) : null}
				<ButtonPrimary
					data-testid='go-to-cart-button'
					fullWidth
					className={styles.button}
					onClick={() => {
						sendClickViewCart()
						navigate(CART_PATH)
					}}
				>
					{replacePlaceholders(t('addToBag.seeBag.button'), {
						1: totalItems ?? 0,
					})}
				</ButtonPrimary>
			</div>
		</div>
	)
}
